import { useState, useEffect, useContext, useCallback } from 'react';
import {useNavigate} from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { filter } from 'lodash';
import { sentenceCase } from 'change-case';
// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Avatar,
  Button,
  Popover,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
// components
import Label from '../components/label';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import { WebhookDeliveriesDrawer } from '../components/webhook-deliveries';
// sections
import { UserListHead, UserListToolbar } from '../sections/@dashboard/user';

import { EntityContext } from '../entity';
import { isEnvelopeOrBatchCancelable } from '../utils/envelopeAndBatchCancelable';
import { getEnvelopeDeleteErrorMessage } from '../utils/envelopeDeleteError';
import { looksLikeUuid } from '../utils/looksLikeUuid';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'name', label: 'Nome', alignRight: false },
  { id: 'company', label: 'Data de criação', alignRight: false },
  { id: 'role', label: 'Ultima alteração', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: '' },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(array, (_envelope) => _envelope.envelope_name.toLowerCase().indexOf(query.toLowerCase()) !== -1);
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function EnvelopesPage() {
  const navigate = useNavigate();

  const [envelopesList, setEnvelopesList] = useState([]);
  const [webhooksDoEnvelope, setWebhooksDoEnvelope] = useState(null); // CHAN-70
  const [loadedPages, setLoadedPages] = useState(new Set([1])); // Páginas já carregadas
  const [totalResults, setTotalResults] = useState(0); // Total real de resultados da API
  const [isLoadingMore, setIsLoadingMore] = useState(false); // Loading para carregamento de mais dados

  const [page, setPage] = useState(0);

  const [order, setOrder] = useState('asc');

  const [selected, setSelected] = useState([]);

  const [orderBy, setOrderBy] = useState('name');

  const [filterName, setFilterName] = useState('');
  const [appliedLookup, setAppliedLookup] = useState('');

  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [deleteEnvelopeError, setDeleteEnvelopeError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [envelopeToDelete, setEnvelopeToDelete] = useState(null);

  const { getEntity, getEnvelopeList, getEnvelope, deleteEnvelope } = useContext(EntityContext)

  const getEnvList = useCallback(async () => {
    getEnvelopeList(1)
      .then((data) => {
        if (data) {
          setEnvelopesList(data.envelopes)
          setTotalResults(data.total_results || 0)
        }
      })
  }, [getEnvelopeList])

  const navigateToNewEnvelope = () => {
    // 👇️ navigate to /contacts
    navigate('/dashboard/envelope');
  };

  useEffect(() => {
    getEntity()
      .then((data) => {
        // setUserList(data.members)
      })
  }, [])

  // Carregar dados iniciais apenas uma vez
  useEffect(() => {
    getEnvList()
  }, []) // Removida a dependência getEnvList

  const hadleDelete = (envelopeKey) => {
    setDeleteEnvelopeError(null);
    deleteEnvelope(envelopeKey)
      .then(() => {
        getEnvList();
      })
      .catch((error) => {
        setDeleteEnvelopeError(getEnvelopeDeleteErrorMessage(error));
      });
  };

  const handleOpenDeleteDialog = (envelope) => {
    setEnvelopeToDelete(envelope);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setEnvelopeToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (!envelopeToDelete) return;
    hadleDelete(envelopeToDelete.envelope_key);
    handleCloseDeleteDialog();
  };

  // Função para carregar mais dados quando necessário
  const loadMoreDataIfNeeded = useCallback(async (currentPage) => {
    // Calcular qual página da API precisamos
    const startIndex = currentPage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    
    // Calcular qual página da API contém o endIndex
    const apiPageNeeded = Math.ceil(endIndex / 50); // 50 é o results_per_page da API
    
    // Verificar se já carregamos essa página e se não excedemos o total
    if (!loadedPages.has(apiPageNeeded) && endIndex <= totalResults && totalResults > 0 && !appliedLookup) {
      setIsLoadingMore(true);
      
      try {
        const data = await getEnvelopeList(apiPageNeeded);
        if (data && data.envelopes) {
          // Adicionar novos dados ao array existente
          setEnvelopesList(prevList => [...prevList, ...data.envelopes]);
          setLoadedPages(prev => new Set([...prev, apiPageNeeded]));
        }
      } catch (error) {
        console.error('Erro ao carregar mais dados:', error);
      } finally {
        setIsLoadingMore(false);
      }
    }
  }, [loadedPages, rowsPerPage, totalResults, appliedLookup]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = envelopesList.map((n) => n.envelope_name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // Verificar se precisamos carregar mais dados
    loadMoreDataIfNeeded(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  // Verificar se precisamos carregar mais dados quando rowsPerPage mudar
  useEffect(() => {
    if (page > 0) {
      loadMoreDataIfNeeded(page);
    }
  }, [rowsPerPage, page]); // Removida a dependência loadMoreDataIfNeeded

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const handleExactSearch = async () => {
    const trimmed = filterName.trim();
    if (!looksLikeUuid(trimmed)) {
      return;
    }
    setPage(0);
    setAppliedLookup(trimmed);
    const data = await getEnvelope(trimmed);
    const envelope = data?.envelope || (data?.envelope_key ? data : null);
    if (envelope && !data?.error) {
      setEnvelopesList([envelope]);
      setTotalResults(1);
    } else {
      setEnvelopesList([]);
      setTotalResults(0);
    }
    setLoadedPages(new Set([1]));
  };

  const handleClearSearch = () => {
    setFilterName('');
    setAppliedLookup('');
    setPage(0);
    setLoadedPages(new Set([1]));
    getEnvList();
  };

  const handleEdit = (envelopeKey) => {
    navigate(`/dashboard/envelope/${envelopeKey}`);
  }

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - envelopesList.length) : 0;

  const filteredUsers = applySortFilter(envelopesList, getComparator(order, orderBy), appliedLookup ? '' : filterName);

  const isNotFound = !filteredUsers.length && !!(appliedLookup || filterName);

  return (
    <>
      <Helmet>
        <title> Envelopes </title>
      </Helmet>

      <Container>
        {deleteEnvelopeError && (
          <Alert severity="error" onClose={() => setDeleteEnvelopeError(null)} sx={{ mb: 2 }}>
            {deleteEnvelopeError}
          </Alert>
        )}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Envelopes
          </Typography>
          <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={navigateToNewEnvelope}>
            Novo envelope
          </Button>
        </Stack>

        <Card>
          <UserListToolbar
            numSelected={selected.length}
            filterName={filterName}
            onFilterName={handleFilterByName}
            onExactSearch={handleExactSearch}
            onClearSearch={handleClearSearch}
            hasExactSearch={Boolean(appliedLookup)}
            searchPlaceholder="UUID do envelope"
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={envelopesList.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                  showCheckbox={false}
                />
                <TableBody>
                  {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    return (
                      <TableRow hover key={row.id} tabIndex={-1}>
                        <TableCell component="th" scope="row" padding="normal">
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Typography variant="subtitle2" noWrap>
                            {row.envelope_name}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell align="left">{row.created_at.substring(0, 10).concat(' ').concat(row.created_at.substring(11, 19))}</TableCell>

                        <TableCell align="left">{row.updated_at ? row.updated_at.substring(0, 10).concat(' ').concat(row.updated_at.substring(11, 19)) : ''}</TableCell>

                        <TableCell align="left">
                          <Label>{row.status}</Label>
                        </TableCell>

                        <TableCell align="right">
                          <IconButton
                            size="large"
                            color="inherit"
                            aria-label="Ver webhooks do envelope"
                            title="Webhooks"
                            onClick={() => setWebhooksDoEnvelope(row)}
                          >
                            <Iconify icon={'mdi:webhook'} />
                          </IconButton>
                          <IconButton size="large" color="inherit" onClick={_ => handleEdit(row.envelope_key)}>
                            <Iconify icon={'eva:edit-fill'} />
                          </IconButton>
                          {isEnvelopeOrBatchCancelable(row.status) ? (
                            <IconButton size="large" color="error" onClick={_ => handleOpenDeleteDialog(row)}>
                              <Iconify icon={'eva:trash-2-outline'} />
                            </IconButton>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={5} />
                    </TableRow>
                  )}
                </TableBody>

                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={5} sx={{ py: 3 }}>
                        <Paper
                          sx={{
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="h6" paragraph>
                            Not found
                          </Typography>

                          <Typography variant="body2">
                            No results found for &nbsp;
                            <strong>&quot;{filterName}&quot;</strong>.
                            <br /> Try checking for typos or using complete words.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          {isLoadingMore && (
            <div style={{ padding: '16px', textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                Carregando mais dados...
              </Typography>
            </div>
          )}

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={appliedLookup ? filteredUsers.length : totalResults}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>

      <WebhookDeliveriesDrawer
        open={Boolean(webhooksDoEnvelope)}
        onClose={() => setWebhooksDoEnvelope(null)}
        envelopeKey={webhooksDoEnvelope?.envelope_key}
        title={webhooksDoEnvelope?.envelope_name}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirmar Exclusão
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Tem certeza que deseja excluir o envelope <strong>{envelopeToDelete?.envelope_name}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" autoFocus>
            Sim, excluir
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
